(() => {
  /** @type {any} */
  const windowAny = window;

  // Public API attached to window
  /** @type {any} */
  const algorithmApi = (windowAny.EcommRecommendationsAlgorithm = windowAny.EcommRecommendationsAlgorithm || {});

  /** @type {any} */
  const utils = windowAny.EcommRecommendationsUtils || {};

  /**
   * Convert metafield/metaobject values into normalized lowercase tokens.
   *
   * @param {any} value
   * @returns {string[]}
   */

  // This function is used to convert thet data that we're passing as data attributes inside of <li> elements of product grid inside of main-collection.liquid
  // Example: data-ecomm-diets='["vegetarisch", "koolhydraatarm"]'
  function convertMetafieldValuesToNormalizedTokens(value) {
    /** @type {string[]} */
    const normalizedTokens = [];

    /** @param {any} rawValue */
    const addNormalizedToken = (rawValue) => {
      const normalizedToken = String(rawValue ?? '').trim().toLowerCase();
      if (!normalizedToken) return;
      normalizedTokens.push(normalizedToken);
    };

    /** @param {any} node */
    const visitNodeAndAddNormalizedTokens = (node) => {
      if (node == null) return;
      if (Array.isArray(node)) return node.forEach(visitNodeAndAddNormalizedTokens);

      const nodeType = typeof node;
      if (nodeType === 'string' || nodeType === 'number' || nodeType === 'boolean') return addNormalizedToken(node);

      if (nodeType === 'object') {
        if ('title' in node) addNormalizedToken(/** @type {any} */(node).title);
        if ('name' in node) addNormalizedToken(/** @type {any} */(node).name);
        if ('label' in node) addNormalizedToken(/** @type {any} */(node).label);
        if ('system' in node && /** @type {any} */ (node).system && 'handle' in /** @type {any} */ (node).system) {
          addNormalizedToken(/** @type {any} */(node).system.handle);
        }
        if ('value' in node) visitNodeAndAddNormalizedTokens(/** @type {any} */(node).value);
      }
    };

    visitNodeAndAddNormalizedTokens(value);
    return Array.from(new Set(normalizedTokens));
  }

  // expose the convertMetafieldValuesToNormalizedTokens function to public
  algorithmApi.convertMetafieldValuesToNormalizedTokens = convertMetafieldValuesToNormalizedTokens;

  // This order list was given by Pieter. We need to use this order list to order the diet tokens based on the frequency of the tokens.
  const DEFAULT_DIET_TIEBREAK_ORDER_TOKENS = ['zoutarm', 'koolhydraatarm', 'Vegetarisch', 'caloriearm', 'eiwitrijk', 'natriumarm'];

  // This function is used to build an ordered list of diet tokens from a `dietType[]` array. Because a profile key can have multiple diet tokens, we need to order them based on the frequency of the tokens.
  /**
   * - Duplicate entries count as quantity (frequency).
   * - Output is sorted by frequency (desc).
   *
   * @param {any[]} dietTypeEntries
   * @param {string[]=} tieBreakOrderTokens
   * @returns {string[]}
   */
  algorithmApi.getDietPreferenceOrderTokens = function getDietPreferenceOrderTokens(dietTypeEntries, tieBreakOrderTokens) {
    // The entries are the diet type entries that are in the profile
    const entries = Array.isArray(dietTypeEntries) ? dietTypeEntries : [];
    // The tie break order tokens are the tie break order tokens that are in the profile
    const tieBreak = Array.isArray(tieBreakOrderTokens) && tieBreakOrderTokens.length
      ? tieBreakOrderTokens.map((x) => String(x || '').trim().toLowerCase()).filter(Boolean)
      : DEFAULT_DIET_TIEBREAK_ORDER_TOKENS;

    /** @type {Record<string, number>} */
    const counts = {};
    entries.forEach((/** @type {any} */ entry) => {
      const token = String(entry?.diet ?? '').trim().toLowerCase();
      if (!token) return;
      counts[token] = (counts[token] || 0) + 1;
    });

    // This function is used to rank the diet tokens based on the tie break order tokens
    /** @param {string} token */
    const rank = (token) => {
      const idx = tieBreak.indexOf(token);
      return idx === -1 ? Number.POSITIVE_INFINITY : idx;
    };

    return Object.keys(counts)
      .sort((a, b) => (Number(counts[b] || 0) - Number(counts[a] || 0)) || (rank(a) - rank(b)))
      .filter(Boolean);
  };

  /**
   * Read the normalized product attributes used for matching.
   * @param {HTMLElement} listItem
   * @returns {{ dietTokens: string[], kitchenTokens: string[], portionGrams: number|null, productTypeTokens: string[], shopifyProductType: string }}
   */
  function readMetaobjectListItemAttributes(listItem) {
    const dietRaw = listItem.getAttribute('data-ecomm-diets');
    const kitchenRaw = listItem.getAttribute('data-ecomm-kitchens');
    const productTypeRaw = listItem.getAttribute('data-ecomm-product-types');
    const shopifyProductType = String(listItem.getAttribute('data-ecomm-shopify-type') || '').trim().toLowerCase();
    const variantTitle = listItem.getAttribute('data-ecomm-variant-title') || '';

    let parsedDiets = null;
    let parsedKitchens = null;
    let parsedProductTypes = null;

    try { parsedDiets = dietRaw ? JSON.parse(dietRaw) : null; } catch { parsedDiets = dietRaw; }
    try { parsedKitchens = kitchenRaw ? JSON.parse(kitchenRaw) : null; } catch { parsedKitchens = kitchenRaw; }
    try { parsedProductTypes = productTypeRaw ? JSON.parse(productTypeRaw) : null; } catch { parsedProductTypes = productTypeRaw; }

    // Extract portion (grams for meals, liters for soups)
    const portionGrams = typeof utils.parsePortionSizeFromText === 'function'
      ? utils.parsePortionSizeFromText(variantTitle)
      : null;


    return {
      dietTokens: convertMetafieldValuesToNormalizedTokens(parsedDiets),
      kitchenTokens: convertMetafieldValuesToNormalizedTokens(parsedKitchens),
      portionGrams,
      productTypeTokens: convertMetafieldValuesToNormalizedTokens(parsedProductTypes),
      shopifyProductType
    };
  }

  /**
   * Classify the item as meal/soup/dessert. Matches snippets/tv-subscription-cart-item-kind.liquid:
   * - soup: custom.product_type contains "extras" AND Shopify product type is "soep" or "soup"
   * - dessert: Shopify product type is "nagerechten"
   * - otherwise => meal
   *
   * @param {{ productTypeTokens: string[], shopifyProductType: string }} attrs
   * @returns {"meal"|"soup"|"dessert"}
   */
  function classifyItemKind(attrs) {
    const hasExtrasToken = attrs.productTypeTokens.includes('extras');
    const isSoepProductType = attrs.shopifyProductType.toLowerCase() === 'soep' || attrs.shopifyProductType.toLowerCase() === 'soup';
    if (hasExtrasToken && isSoepProductType) return 'soup';
    if (attrs.shopifyProductType.toLowerCase() === 'nagerechten' || attrs.shopifyProductType.toLowerCase() === 'toetjes') return 'dessert';
    return 'meal';
  }

  /**
   * Profile matching (AND logic):
   * - If profile specifies diets and product has diets -> must intersect
   * - If profile specifies kitchens and product has kitchens -> must intersect
   * - If profile specifies grams and product has grams -> must match
   *
   * Hard checks for parent diet types:
   * - If profile has ONLY "vegetarisch" -> only show products with "vegetarisch"
   * - If profile has ONLY "koolhydraatarm" -> only show products with "koolhydraatarm"
   * - If profile has "ik volg geen dieet" -> exclude products with "vegetarisch" or "koolhydraatarm"
   *
   * If product is missing a dimension, we do NOT exclude it (keeps results from going empty).
   *
   * @param {{ dietTokens: string[], kitchenTokens: string[], portionGrams: number|null }} profile
   * @param {{ dietTokens: string[], kitchenTokens: string[], portionGrams: number|null }} item
   * @returns {boolean}
   */
  /**
   * Check if item matches profile based on diet tokens only (ignores kitchen and portion grams).
   * Used for stableMeals fallback to ensure diet requirements are met.
   *
   * @param {{ dietTokens: string[] }} profile
   * @param {{ dietTokens: string[] }} item
   * @returns {boolean}
   */
  function doesItemMatchProfileDietOnly(profile, item) {
    const profileDietTokens = profile.dietTokens || [];
    const itemDietTokens = item.dietTokens || [];
    const hasNoDietToken = profileDietTokens.includes('ik volg geen dieet');
    const hasVegetarisch = profileDietTokens.includes('vegetarisch');
    const hasKoolhydraatarm = profileDietTokens.includes('koolhydraatarm');

    // Special case: "ik volg geen dieet" means show all except vegetarisch/koolhydraatarm (unless explicitly requested)
    if (hasNoDietToken) {
      if (itemDietTokens.length > 0) {
        if (itemDietTokens.includes('vegetarisch') && !hasVegetarisch) return false;
        if (itemDietTokens.includes('koolhydraatarm') && !hasKoolhydraatarm) return false;
      }
      return true; // Skip other checks for "ik volg geen dieet"
    }

    // Hard checks for parent diet types: vegetarisch and koolhydraatarm
    if (hasVegetarisch && hasKoolhydraatarm) {
      // Both selected: require product to have at least one (OR logic)
      if (itemDietTokens.length === 0 || (!itemDietTokens.includes('vegetarisch') && !itemDietTokens.includes('koolhydraatarm'))) {
        return false;
      }
    } else if (hasVegetarisch) {
      // Only vegetarisch: require product to have vegetarisch
      if (itemDietTokens.length === 0 || !itemDietTokens.includes('vegetarisch')) {
        return false;
      }
    } else if (hasKoolhydraatarm) {
      // Only koolhydraatarm: require product to have koolhydraatarm
      if (itemDietTokens.length === 0 || !itemDietTokens.includes('koolhydraatarm')) {
        return false;
      }
    }

    // Standard intersection: profile and item diets must overlap
    if (profileDietTokens.length && itemDietTokens.length) {
      if (!profileDietTokens.some((token) => itemDietTokens.includes(token))) {
        return false;
      }
    }

    return true;
  }

  /**
   *
   * @param {{ dietTokens: string[], kitchenTokens: string[], portionGrams: number|null, mealSlots?: { dietToken: string, portionGrams: number|null }[] }} profile
   * @param {{ dietTokens: string[], kitchenTokens: string[], portionGrams: number|null, shopifyProductType?: string }} item
   * @param {HTMLElement|null} listItem - Optional list item element for checking variant availability
   * @returns {boolean}
   */
  function doesItemMatchProfile(profile, item, listItem = null) {
    const profileKitchenTokens = profile.kitchenTokens || [];
    const itemKitchenTokens = item.kitchenTokens || [];

    // Check diet matching using the dedicated function
    if (!doesItemMatchProfileDietOnly(profile, item)) {
      return false;
    }

    // Kitchen matching: simple array intersection (like diet matching)
    if (profileKitchenTokens.length && itemKitchenTokens.length) {
      const intersects = profileKitchenTokens.some((token) => itemKitchenTokens.includes(token));
      if (!intersects) {
        return false;
      }
    }


    // Only check portion grams if:
    // 1) Profile has portionGrams directly (not mealSlots)
    // 2) Item is a meal (not soup/dessert)
    const portionGramsToCheck = profile.portionGrams;
    const itemType = (/** @type {any} */ item).shopifyProductType || '';
    const isMeal = itemType.toLowerCase().includes('maaltijd');

    if (portionGramsToCheck && isMeal) {
      // If we have DOM, prefer checking the variant dropdown for an in-stock matching grams variant.
      // Only do this if the dropdown exists; otherwise fall back to the item's portionGrams field.
      const hasDropdown = (() => {
        if (!listItem) return false;
        const productId = listItem.getAttribute('data-product-id');
        const productCard = listItem.closest('product-card') || listItem.querySelector('product-card');
        const variantDropdown = productCard?.querySelector(`variant-select-dropdown[data-product-id="${productId}"]`);
        return Boolean(variantDropdown);
      })();

      if (hasDropdown && listItem) {
        if (!hasVariantWithPortionGramsAvailable(listItem, portionGramsToCheck)) return false;
        return true;
      }

      // Fallback: if we can't find the dropdown, check if current variant matches
      if (
        item.portionGrams &&
        !(typeof utils.isSamePortionSize === 'function' && utils.isSamePortionSize(portionGramsToCheck, item.portionGrams))
      ) {
        return false;
      }
    }

    return true;
  }

  /**
   * Check if a product has a variant with the desired portion grams that's available.
   * Similar to diet/kitchen matching, but checks variant dropdown for availability.
   * 
   * @param {HTMLElement} listItem - The list item element
   * @param {number|null} portionGrams - The desired portion grams
   * @returns {boolean} - true if variant with desired portion grams is available, or if no requirement
   */
  function hasVariantWithPortionGramsAvailable(listItem, portionGrams) {
    // No portion requirement = always match (like diet/kitchen)
    if (portionGrams === null || !listItem) return true;

    // Find variant dropdown (where all variants are listed)
    const productId = listItem.getAttribute('data-product-id');
    const productCard = listItem.closest('product-card') || listItem.querySelector('product-card');
    const variantDropdown = productCard?.querySelector(`variant-select-dropdown[data-product-id="${productId}"]`);

    if (!variantDropdown) return true; // Can't check = assume match

    // Check each variant: does it have the desired portion grams AND is it available?
    const variantOptions = variantDropdown.querySelectorAll('.dropdown-menu-item[data-variant-title]');

    for (const option of variantOptions) {
      const variantTitle = option.getAttribute('data-variant-title') || '';
      const variantGrams = typeof utils.parsePortionSizeFromText === 'function'
        ? utils.parsePortionSizeFromText(variantTitle)
        : null;

      // Does this variant have the desired portion grams?
      if (variantGrams !== null && typeof utils.isSamePortionSize === 'function' && utils.isSamePortionSize(variantGrams, portionGrams)) {
        // Is it available? (either marked as available OR has inventory)
        const isAvailable = option.getAttribute('data-variant-available') === 'true';
        const hasInventory = (() => {
          const inventoryRaw = option.getAttribute('data-variant-inventory-levels');
          if (!inventoryRaw) return false;
          try {
            const inventory = JSON.parse(inventoryRaw);
            return Array.isArray(inventory) && inventory.length > 0;
          } catch {
            return false;
          }
        })();

        if (isAvailable || hasInventory) {
          return true; // Found matching variant that's available!
        }
      }
    }

    return false; // No matching variant found or none are available
  }

  /**
   * Score for sorting (higher is better) within a bucket.
   * Diet match has the most weight.
   *
   * @param {{ dietTokens: string[], kitchenTokens: string[], portionGrams: number|null }} profile
   * @param {{ dietTokens: string[], kitchenTokens: string[], portionGrams: number|null }} item
   * @returns {number}
   */
  function scoreItemForProfile(profile, item) {
    let score = 0;
    // Diet match has the most weight of +3
    if (profile.dietTokens.length && item.dietTokens.length && profile.dietTokens.some((t) => item.dietTokens.includes(t))) score += 3;
    // Kitchen match has the second most weight of +2
    if (profile.kitchenTokens.length && item.kitchenTokens.length && profile.kitchenTokens.some((t) => item.kitchenTokens.includes(t))) score += 2;
    // Portion grams match has the least weight of +1
    if (profile.portionGrams && item.portionGrams && typeof utils.isSamePortionSize === 'function' && utils.isSamePortionSize(profile.portionGrams, item.portionGrams)) score += 1;
    return score;
  }

  /**
   * Pick guest metaobject items for meal/soup/dessert and return an ordering,
   * without mutating the DOM (caller can mark + reorder).
   *
   * @param {HTMLElement[]} listItems
   * @param {{ dietTokens: string[], kitchenTokens: string[], portionGrams: number|null, mealSlots?: { dietToken: string, portionGrams: number|null }[], soupSlots?: { portionGrams: number|null }[], mealsCount: number, soupCount: number, dessertCount: number }} profilePreferences
   * @returns {{ orderedChosen: HTMLElement[], orderedRest: HTMLElement[], chosenDietByElement: { element: HTMLElement, dietToken: string }[], chosenPortionGramsByElement: { element: HTMLElement, portionGrams: number }[], pickedMeals: number, pickedSoup: number, pickedDessert: number, totalPicked: number }}
   */
  algorithmApi.pickMealsAndExtrasBasedOnProfileKeyPreference = function pickMealsAndExtrasBasedOnProfileKeyPreference(listItems, profilePreferences) {

    const items = listItems.filter((el) => el instanceof HTMLElement);

    // Enrich the items with the additional information like item kind, matches, score, etc.
    /** @type {{ element: HTMLElement, originalIndex: number, itemKind: "meal"|"soup"|"dessert", attrs: { dietTokens: string[], kitchenTokens: string[], portionGrams: number|null, productTypeTokens: string[], shopifyProductType: string }, matches: boolean, score: number }[]} */
    const enrichedItems = items.map((element, arrayIndex) => {
      const attrs = readMetaobjectListItemAttributes(element);
      const itemKind = classifyItemKind(attrs);
      const matches = doesItemMatchProfile(profilePreferences, attrs, element);
      const score = matches ? scoreItemForProfile(profilePreferences, attrs) : -1;
      
      const originalIndex = element.dataset.ecommOriginalIndex 
        ? Number(element.dataset.ecommOriginalIndex) 
        : arrayIndex;
      return { element, originalIndex, itemKind, attrs, matches, score };
    });

    // Sort the items by the score
    const sortedItemsByScore = enrichedItems
      .filter((x) => x.matches)
      .sort((a, b) => (b.score - a.score) || (a.originalIndex - b.originalIndex));

    /** @type {{ element: HTMLElement, originalIndex: number, itemKind: "meal"|"soup"|"dessert", matches: boolean, score: number }[]} */
    const chosenItems = [];
    // Keep track of the chosen elements
    const chosenElements = new Set();
    // Keep track of the chosen diet tokens
    /** @type {Map<HTMLElement, string>} */
    const chosenDietTokenMap = new Map();
    // Keep track of the chosen portion grams (only for meal slot picks that specify grams)
    /** @type {Map<HTMLElement, number>} */
    const chosenPortionGramsMap = new Map();

    /**
     * Fill a bucket (meal/soup/dessert).
     * Only include items that match the profile (respects hard diet checks).
     *
     * @param {"meal"|"soup"|"dessert"} itemKind
     * @param {number} desiredCount
     */
    function fillBucketWithItems(itemKind, desiredCount) {
      if (!desiredCount) return;

      for (const item of sortedItemsByScore) {
        if (item.itemKind !== itemKind) continue;
        if (chosenElements.has(item.element)) continue;
        chosenItems.push(item);
        chosenElements.add(item.element);
        // For soup/dessert (and any other chosen items), carry forward the portion size derived from the variant title
        // (e.g. "halve liter" => 0.5, "hele liter" => 1, "400gr" => 400).
        if (!chosenPortionGramsMap.has(item.element) && Number.isFinite(Number(item.attrs?.portionGrams)) && Number(item.attrs.portionGrams) > 0) {
          chosenPortionGramsMap.set(item.element, Number(item.attrs.portionGrams));
        }
        if (chosenItems.filter((x) => x.itemKind === itemKind).length >= desiredCount) return;
      }

      // Fallback: only include matching items (respects hard diet checks) - because vega profile keys can get meat 
      // so always include matching items
      const allSameTypeMatching = enrichedItems
        // Filter the items by the item kind and the matches
        .filter((x) => x.itemKind === itemKind && x.matches)
        // Sort the items by the original index
        .sort((a, b) => a.originalIndex - b.originalIndex);

      for (const item of allSameTypeMatching) {
        if (chosenElements.has(item.element)) continue;
        chosenItems.push(item);
        chosenElements.add(item.element);
        if (!chosenPortionGramsMap.has(item.element) && Number.isFinite(Number(item.attrs?.portionGrams)) && Number(item.attrs.portionGrams) > 0) {
          chosenPortionGramsMap.set(item.element, Number(item.attrs.portionGrams));
        }
        if (chosenItems.filter((x) => x.itemKind === itemKind).length >= desiredCount) return;
      }
    }

    // Meals: if mealSlots are provided, pick slot-by-slot to respect diet quantity + grams.
    // How many meals do we want to pick per profile key?
    const desiredMeals = Number(profilePreferences.mealsCount || 0) || 0;
    // Get the meal slots from the profile preferences
    const mealSlots = Array.isArray(profilePreferences?.mealSlots) ? profilePreferences.mealSlots : [];
    // If we want to pick meals and we have meal slots, then we need to pick the meal slots
    if (desiredMeals && mealSlots.length) {
      // Get the meal slots from the profile preferences
      const slots = mealSlots.slice(0, desiredMeals);
      // Get the meal candidates from the enriched items
      const mealItems = enrichedItems.filter((x) => x.itemKind === 'meal');

      /**
       * Score a candidate for a specific meal slot.
       * @param {{ dietToken: string, portionGrams: number|null }} slot
       * @param {{ dietTokens: string[], kitchenTokens: string[], portionGrams: number|null }} attrs
       */
      const scoreMealSlot = (slot, attrs) => {
        let score = 0;
        // Diet: strong preference for exact diet token (if product has diets)
        if (slot.dietToken && attrs.dietTokens.length) {
          score += attrs.dietTokens.includes(slot.dietToken) ? 6 : -6;
        }
        // Kitchens: intersect
        if (profilePreferences.kitchenTokens.length && attrs.kitchenTokens.length) {
          if (profilePreferences.kitchenTokens.some((t) => attrs.kitchenTokens.includes(t))) score += 3;
        }
        // Grams: exact match bonus, mismatch penalty when both present
        if (slot.portionGrams && attrs.portionGrams) {
          score += (typeof utils.isSamePortionSize === 'function' && utils.isSamePortionSize(slot.portionGrams, attrs.portionGrams)) ? 3 : -2;
        }
        return score;
      };

      for (const slot of slots) {
        // Portion grams check: similar to diet/kitchen, but checks variant availability
        // If slot has portionGrams, product must have that variant in stock
        const matchesPortionGrams = (/** @type {any} */ c) => slot.portionGrams === null || hasVariantWithPortionGramsAvailable(c.element, slot.portionGrams);

        // Pass 1: prefer items that match diet AND portion grams
        const primary = mealItems
        // Filter the items by the chosen elements
          .filter((c) => !chosenElements.has(c.element))
          .filter((c) => c.matches) // Respect hard diet checks
          .filter((c) => !slot.dietToken || !c.attrs.dietTokens.length || c.attrs.dietTokens.includes(slot.dietToken))
          .filter(matchesPortionGrams) // Check portion grams (like diet/kitchen check)
          .map((c) => ({ c, score: scoreMealSlot(slot, c.attrs) }))
          .sort((a, b) => (b.score - a.score) || (a.c.originalIndex - b.c.originalIndex));

        // Pass 2: fallback (only matching items that respect hard diet checks)
        const secondary = mealItems
          .filter((c) => !chosenElements.has(c.element))
          .filter((c) => c.matches) // Respect hard diet checks
          // .filter(matchesPortionGrams)
          .map((c) => ({ c, score: scoreMealSlot(slot, c.attrs) }))
          .sort((a, b) => (b.score - a.score) || (a.c.originalIndex - b.c.originalIndex));

        const picked = primary[0]?.c || secondary[0]?.c || null;
        if (!picked) break;
        chosenItems.push(picked);
        chosenElements.add(picked.element);
        if (slot.dietToken) chosenDietTokenMap.set(picked.element, String(slot.dietToken).trim().toLowerCase());
        if (slot.portionGrams && Number.isFinite(Number(slot.portionGrams))) {
          chosenPortionGramsMap.set(picked.element, Number(slot.portionGrams));
        }
      }

      // If we still need meals (not enough items), fill remaining meals in stable order (only diet-matched items).
      const remainingMealsNeeded = desiredMeals - chosenItems.filter((x) => x.itemKind === 'meal').length;
      if (remainingMealsNeeded > 0) {
        const allMealItems = mealItems.filter((c) => !chosenElements.has(c.element));

        // For stableMeals, only check diet matches (ignore kitchen and portion grams) only if it's a meal
        const stableMeals = allMealItems
          .filter((c) => doesItemMatchProfileDietOnly(profilePreferences, c.attrs))
          .sort((a, b) => a.originalIndex - b.originalIndex);

        for (const c of stableMeals) {
          chosenItems.push(c);
          chosenElements.add(c.element);
          if (chosenItems.filter((x) => x.itemKind === 'meal').length >= desiredMeals) break;
        }
      }

    } else {
      fillBucketWithItems('meal', desiredMeals);
    }

    // Soups: if soupSlots are provided (from addOns), pick slot-by-slot (0.5L vs 1L).
    const desiredSoups = Number(profilePreferences.soupCount || 0) || 0;
    const soupSlots = Array.isArray(profilePreferences?.soupSlots) ? profilePreferences.soupSlots : [];
    if (desiredSoups && soupSlots.length) {
      const slots = soupSlots.slice(0, desiredSoups);
      const soupItems = enrichedItems.filter((x) => x.itemKind === 'soup');

      for (const slot of slots) {
        const portion = slot?.portionGrams ?? null;
        const candidates = soupItems
          .filter((c) => !chosenElements.has(c.element))
          .filter((c) => c.matches) // Respect diet/kitchen checks
          .filter((c) => portion === null || hasVariantWithPortionGramsAvailable(c.element, portion))
          .sort((a, b) => a.originalIndex - b.originalIndex);

        const picked = candidates[0] || null;
        if (!picked) break;
        chosenItems.push(picked);
        chosenElements.add(picked.element);
        if (portion !== null && Number.isFinite(Number(portion)) && Number(portion) > 0) {
          chosenPortionGramsMap.set(picked.element, Number(portion));
        } else if (!chosenPortionGramsMap.has(picked.element) && Number.isFinite(Number(picked.attrs?.portionGrams)) && Number(picked.attrs.portionGrams) > 0) {
          chosenPortionGramsMap.set(picked.element, Number(picked.attrs.portionGrams));
        }
      }

      const remainingSoupsNeeded = desiredSoups - chosenItems.filter((x) => x.itemKind === 'soup').length;
      if (remainingSoupsNeeded > 0) fillBucketWithItems('soup', remainingSoupsNeeded);
    } else {
      fillBucketWithItems('soup', desiredSoups);
    }
    fillBucketWithItems('dessert', Number(profilePreferences.dessertCount || 0) || 0);

    const orderedChosen = chosenItems.map((x) => x.element);
    // Rest products: show all items, but prioritize matching ones (sorted by score), then non-matching ones
    const orderedRest = enrichedItems
      .filter((x) => !chosenElements.has(x.element))
      .sort((a, b) => {
        // First, prioritize matching items over non-matching
        if (a.matches && !b.matches) return -1;
        if (!a.matches && b.matches) return 1;
        // If both match or both don't match, sort by score (if matching) or original index
        if (a.matches && b.matches) {
          return (b.score - a.score) || (a.originalIndex - b.originalIndex);
        }
        // Both don't match, keep original order
        return a.originalIndex - b.originalIndex;
      })
      .map((x) => x.element);

    const pickedMeals = chosenItems.filter((x) => x.itemKind === 'meal').length;
    const pickedSoup = chosenItems.filter((x) => x.itemKind === 'soup').length;
    const pickedDessert = chosenItems.filter((x) => x.itemKind === 'dessert').length;

    return {
      orderedChosen,
      orderedRest,
      chosenDietByElement: Array.from(chosenDietTokenMap.entries()).map(([element, dietToken]) => ({ element, dietToken })),
      chosenPortionGramsByElement: Array.from(chosenPortionGramsMap.entries()).map(([element, portionGrams]) => ({ element, portionGrams })),
      pickedMeals,
      pickedSoup,
      pickedDessert,
      totalPicked: chosenItems.length,
    };
  };
})();


